'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { 
  useDebugLogger, 
  LogLevel, 
  LogEntry, 
  NetworkLog, 
  collectDeviceInfo, 
  DeviceInfo 
} from '../hooks/useDebugLogger';

// ============================================
// ESTILOS INLINE (para funcionar sem CSS externo)
// ============================================

const styles = {
  container: {
    position: 'fixed' as const,
    inset: 0,
    zIndex: 99999,
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: '#0f172a',
    color: '#e2e8f0',
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    fontSize: '12px',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: '#1e293b',
    borderBottom: '1px solid #334155',
    flexShrink: 0,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 700,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 12px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  primaryButton: {
    backgroundColor: '#3b82f6',
    color: '#fff',
  },
  secondaryButton: {
    backgroundColor: '#374151',
    color: '#e2e8f0',
  },
  dangerButton: {
    backgroundColor: '#dc2626',
    color: '#fff',
  },
  successButton: {
    backgroundColor: '#16a34a',
    color: '#fff',
  },
  tabs: {
    display: 'flex',
    backgroundColor: '#1e293b',
    borderBottom: '1px solid #334155',
    padding: '0 16px',
    flexShrink: 0,
    overflowX: 'auto' as const,
  },
  tab: {
    padding: '10px 16px',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    color: '#94a3b8',
    whiteSpace: 'nowrap' as const,
    transition: 'all 0.2s',
  },
  tabActive: {
    color: '#3b82f6',
    borderBottomColor: '#3b82f6',
  },
  filters: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#1e293b',
    borderBottom: '1px solid #334155',
    flexWrap: 'wrap' as const,
    flexShrink: 0,
  },
  filterChip: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '10px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '8px',
  },
  logEntry: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '6px 8px',
    borderRadius: '4px',
    marginBottom: '2px',
    fontSize: '11px',
    lineHeight: 1.4,
  },
  logTime: {
    flexShrink: 0,
    width: '75px',
    color: '#64748b',
    fontSize: '10px',
  },
  logLevel: {
    flexShrink: 0,
    width: '55px',
    fontWeight: 700,
    fontSize: '9px',
    textTransform: 'uppercase' as const,
  },
  logCategory: {
    flexShrink: 0,
    width: '100px',
    color: '#8b5cf6',
    fontSize: '10px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  logMessage: {
    flex: 1,
    wordBreak: 'break-word' as const,
    whiteSpace: 'pre-wrap' as const,
  },
  logData: {
    marginTop: '4px',
    padding: '6px',
    backgroundColor: '#0f172a',
    borderRadius: '4px',
    fontSize: '10px',
    whiteSpace: 'pre-wrap' as const,
    overflow: 'auto',
    maxHeight: '200px',
  },
  deviceInfoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '12px',
    padding: '16px',
  },
  deviceInfoCard: {
    backgroundColor: '#1e293b',
    borderRadius: '8px',
    padding: '12px',
    border: '1px solid #334155',
  },
  deviceInfoTitle: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#3b82f6',
    marginBottom: '8px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  deviceInfoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
    borderBottom: '1px solid #334155',
    fontSize: '11px',
  },
  deviceInfoLabel: {
    color: '#94a3b8',
  },
  deviceInfoValue: {
    color: '#e2e8f0',
    fontWeight: 600,
  },
  networkEntry: {
    backgroundColor: '#1e293b',
    borderRadius: '6px',
    marginBottom: '8px',
    overflow: 'hidden',
  },
  networkHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  networkMethod: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 700,
  },
  networkUrl: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: '11px',
  },
  networkStatus: {
    fontWeight: 700,
    fontSize: '11px',
  },
  networkDetails: {
    padding: '12px',
    backgroundColor: '#0f172a',
    borderTop: '1px solid #334155',
    fontSize: '10px',
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '12px',
    padding: '16px',
  },
  statCard: {
    backgroundColor: '#1e293b',
    borderRadius: '8px',
    padding: '16px',
    textAlign: 'center' as const,
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#3b82f6',
  },
  statLabel: {
    fontSize: '10px',
    color: '#94a3b8',
    marginTop: '4px',
    textTransform: 'uppercase' as const,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    color: '#64748b',
  },
  copySuccess: {
    position: 'fixed' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#16a34a',
    color: '#fff',
    padding: '16px 32px',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 700,
    zIndex: 100000,
    animation: 'fadeInOut 2s ease-in-out',
  },
};

// Cores por nível de log
const levelColors: Record<LogLevel, { bg: string; text: string }> = {
  log: { bg: '#374151', text: '#9ca3af' },
  info: { bg: '#1e3a5f', text: '#60a5fa' },
  warn: { bg: '#713f12', text: '#fbbf24' },
  error: { bg: '#7f1d1d', text: '#f87171' },
  debug: { bg: '#3f3f46', text: '#a1a1aa' },
  network: { bg: '#164e63', text: '#22d3ee' },
  event: { bg: '#4c1d95', text: '#a78bfa' },
  state: { bg: '#14532d', text: '#4ade80' },
  sip: { bg: '#581c87', text: '#c084fc' },
};

const levelIcons: Record<LogLevel, string> = {
  log: '📝',
  info: 'ℹ️',
  warn: '⚠️',
  error: '❌',
  debug: '🔍',
  network: '🌐',
  event: '⚡',
  state: '📊',
  sip: '📞',
};

type TabType = 'logs' | 'network' | 'device' | 'stats';

interface DebugPanelProps {
  onClose: () => void;
}

export function DebugPanel({ onClose }: DebugPanelProps) {
  const { logs, networkLogs, clear, copyToClipboard } = useDebugLogger();
  const [activeTab, setActiveTab] = useState<TabType>('logs');
  const [levelFilters, setLevelFilters] = useState<Set<LogLevel>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNetworkIds, setExpandedNetworkIds] = useState<Set<number>>(new Set());
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [expandedLogIds, setExpandedLogIds] = useState<Set<number>>(new Set());

  // Carregar device info
  useEffect(() => {
    collectDeviceInfo().then(setDeviceInfo);
  }, []);

  // Filtrar logs
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      // Filtro por nível
      if (levelFilters.size > 0 && !levelFilters.has(log.level)) {
        return false;
      }
      // Filtro por categoria
      if (categoryFilter && !log.category.toLowerCase().includes(categoryFilter.toLowerCase())) {
        return false;
      }
      // Filtro por busca
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          log.message.toLowerCase().includes(query) ||
          log.category.toLowerCase().includes(query) ||
          (log.data && JSON.stringify(log.data).toLowerCase().includes(query))
        );
      }
      return true;
    });
  }, [logs, levelFilters, categoryFilter, searchQuery]);

  // Categorias únicas
  const categories = useMemo(() => {
    const cats = new Set(logs.map((l) => l.category));
    return Array.from(cats).sort();
  }, [logs]);

  // Stats
  const stats = useMemo(() => {
    const byLevel: Record<string, number> = {};
    const byCategory: Record<string, number> = {};
    
    for (const log of logs) {
      byLevel[log.level] = (byLevel[log.level] || 0) + 1;
      byCategory[log.category] = (byCategory[log.category] || 0) + 1;
    }
    
    const networkSuccess = networkLogs.filter((n) => n.status && n.status >= 200 && n.status < 400).length;
    const networkError = networkLogs.filter((n) => !n.status || n.status >= 400).length;
    const avgDuration = networkLogs.length > 0
      ? Math.round(networkLogs.reduce((sum, n) => sum + (n.duration || 0), 0) / networkLogs.length)
      : 0;
    
    return { byLevel, byCategory, networkSuccess, networkError, avgDuration };
  }, [logs, networkLogs]);

  // Toggle level filter
  const toggleLevelFilter = useCallback((level: LogLevel) => {
    setLevelFilters((prev) => {
      const next = new Set(prev);
      if (next.has(level)) {
        next.delete(level);
      } else {
        next.add(level);
      }
      return next;
    });
  }, []);

  // Toggle network details
  const toggleNetworkDetails = useCallback((id: number) => {
    setExpandedNetworkIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Toggle log data
  const toggleLogData = useCallback((id: number) => {
    setExpandedLogIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Handle copy
  const handleCopy = useCallback(async () => {
    const success = await copyToClipboard();
    if (success) {
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 2000);
    }
  }, [copyToClipboard]);

  // Formatar timestamp
  const formatTime = (date: Date) => {
    return date.toTimeString().split(' ')[0] + '.' + date.getMilliseconds().toString().padStart(3, '0');
  };

  // Render log entry
  const renderLogEntry = (log: LogEntry) => {
    const colors = levelColors[log.level];
    const isExpanded = expandedLogIds.has(log.id);
    const hasData = log.data !== undefined || log.stack;
    
    return (
      <div 
        key={log.id}
        style={{
          ...styles.logEntry,
          backgroundColor: colors.bg + '40',
          cursor: hasData ? 'pointer' : 'default',
        }}
        onClick={() => hasData && toggleLogData(log.id)}
      >
        <span style={styles.logTime}>{formatTime(log.timestamp)}</span>
        <span style={{ ...styles.logLevel, color: colors.text }}>
          {levelIcons[log.level]} {log.level}
        </span>
        <span style={styles.logCategory}>[{log.category}]</span>
        <div style={{ flex: 1 }}>
          <span style={styles.logMessage}>{log.message}</span>
          {hasData && (
            <span style={{ color: '#64748b', fontSize: '10px', marginLeft: '8px' }}>
              {isExpanded ? '▼' : '▶'} {hasData ? 'data' : ''}
            </span>
          )}
          {isExpanded && (
            <div style={styles.logData}>
              {log.data !== undefined && (
                <pre>{(() => {
                  try {
                    return JSON.stringify(log.data, null, 2);
                  } catch {
                    return '[Não serializável]';
                  }
                })()}</pre>
              )}
              {log.stack && (
                <pre style={{ color: '#f87171', marginTop: log.data ? '8px' : 0 }}>
                  {log.stack}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render network entry
  const renderNetworkEntry = (log: NetworkLog) => {
    const isSuccess = log.status && log.status >= 200 && log.status < 400;
    const isExpanded = expandedNetworkIds.has(log.id);
    
    const methodColors: Record<string, string> = {
      GET: '#22c55e',
      POST: '#3b82f6',
      PUT: '#f59e0b',
      DELETE: '#ef4444',
      PATCH: '#8b5cf6',
    };
    
    return (
      <div key={log.id} style={styles.networkEntry}>
        <div 
          style={{
            ...styles.networkHeader,
            backgroundColor: isExpanded ? '#334155' : 'transparent',
          }}
          onClick={() => toggleNetworkDetails(log.id)}
        >
          <span style={{ color: '#64748b', fontSize: '10px' }}>
            {formatTime(log.timestamp)}
          </span>
          <span style={{
            ...styles.networkMethod,
            backgroundColor: (methodColors[log.method] || '#6b7280') + '30',
            color: methodColors[log.method] || '#6b7280',
          }}>
            {log.method}
          </span>
          <span style={styles.networkUrl} title={log.url}>
            {log.url}
          </span>
          <span style={{
            ...styles.networkStatus,
            color: isSuccess ? '#22c55e' : '#ef4444',
          }}>
            {log.status || 'FAILED'}
          </span>
          <span style={{ color: '#64748b', fontSize: '10px' }}>
            {log.duration ? `${log.duration}ms` : '-'}
          </span>
          <span style={{ color: '#64748b' }}>
            {isExpanded ? '▼' : '▶'}
          </span>
        </div>
        {isExpanded && (
          <div style={styles.networkDetails}>
            {log.error && (
              <div style={{ color: '#f87171', marginBottom: '8px' }}>
                <strong>Error:</strong> {log.error}
              </div>
            )}
            {log.requestHeaders && Object.keys(log.requestHeaders).length > 0 && (
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ color: '#94a3b8' }}>Request Headers:</strong>
                <pre style={{ marginTop: '4px' }}>
                  {JSON.stringify(log.requestHeaders, null, 2)}
                </pre>
              </div>
            )}
            {log.requestBody && (
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ color: '#94a3b8' }}>Request Body:</strong>
                <pre style={{ marginTop: '4px' }}>{log.requestBody}</pre>
              </div>
            )}
            {log.responseHeaders && Object.keys(log.responseHeaders).length > 0 && (
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ color: '#94a3b8' }}>Response Headers:</strong>
                <pre style={{ marginTop: '4px' }}>
                  {JSON.stringify(log.responseHeaders, null, 2)}
                </pre>
              </div>
            )}
            {log.responseBody && (
              <div>
                <strong style={{ color: '#94a3b8' }}>Response Body:</strong>
                <pre style={{ marginTop: '4px' }}>{log.responseBody}</pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          <span>🔧</span>
          <span>Debug Console</span>
          <span style={{ 
            fontSize: '10px', 
            backgroundColor: '#374151', 
            padding: '2px 8px', 
            borderRadius: '10px' 
          }}>
            {logs.length} logs
          </span>
        </div>
        <div style={styles.headerActions}>
          <button
            style={{ ...styles.button, ...styles.successButton }}
            onClick={handleCopy}
          >
            📋 Copiar Todos
          </button>
          <button
            style={{ ...styles.button, ...styles.dangerButton }}
            onClick={clear}
          >
            🗑️ Limpar
          </button>
          <button
            style={{ ...styles.button, ...styles.primaryButton }}
            onClick={onClose}
          >
            ← Voltar
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <div
          style={{
            ...styles.tab,
            ...(activeTab === 'logs' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('logs')}
        >
          📝 Logs ({logs.length})
        </div>
        <div
          style={{
            ...styles.tab,
            ...(activeTab === 'network' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('network')}
        >
          🌐 Network ({networkLogs.length})
        </div>
        <div
          style={{
            ...styles.tab,
            ...(activeTab === 'device' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('device')}
        >
          📱 Device
        </div>
        <div
          style={{
            ...styles.tab,
            ...(activeTab === 'stats' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('stats')}
        >
          📊 Stats
        </div>
      </div>

      {/* Filters for logs tab */}
      {activeTab === 'logs' && (
        <div style={styles.filters}>
          <input
            type="text"
            placeholder="🔍 Buscar..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #374151',
              backgroundColor: '#0f172a',
              color: '#e2e8f0',
              fontSize: '11px',
              width: '150px',
            }}
          />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #374151',
              backgroundColor: '#0f172a',
              color: '#e2e8f0',
              fontSize: '11px',
            }}
          >
            <option value="">Todas categorias</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {(Object.keys(levelColors) as LogLevel[]).map((level) => {
              const isActive = levelFilters.has(level);
              const colors = levelColors[level];
              return (
                <span
                  key={level}
                  style={{
                    ...styles.filterChip,
                    backgroundColor: isActive ? colors.text : '#374151',
                    color: isActive ? '#000' : '#94a3b8',
                  }}
                  onClick={() => toggleLevelFilter(level)}
                >
                  {levelIcons[level]} {level}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Content */}
      <div style={styles.content}>
        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <>
            {filteredLogs.length === 0 ? (
              <div style={styles.emptyState}>
                <span style={{ fontSize: '48px', marginBottom: '16px' }}>📭</span>
                <span>Nenhum log encontrado</span>
              </div>
            ) : (
              filteredLogs.slice(-200).reverse().map(renderLogEntry)
            )}
          </>
        )}

        {/* Network Tab */}
        {activeTab === 'network' && (
          <>
            {networkLogs.length === 0 ? (
              <div style={styles.emptyState}>
                <span style={{ fontSize: '48px', marginBottom: '16px' }}>🌐</span>
                <span>Nenhuma requisição registrada</span>
              </div>
            ) : (
              [...networkLogs].reverse().map(renderNetworkEntry)
            )}
          </>
        )}

        {/* Device Tab */}
        {activeTab === 'device' && deviceInfo && (
          <div style={styles.deviceInfoGrid}>
            <div style={styles.deviceInfoCard}>
              <div style={styles.deviceInfoTitle}>📱 Sistema</div>
              <div style={styles.deviceInfoRow}>
                <span style={styles.deviceInfoLabel}>User Agent</span>
                <span style={{ ...styles.deviceInfoValue, fontSize: '9px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {deviceInfo.userAgent.substring(0, 50)}...
                </span>
              </div>
              <div style={styles.deviceInfoRow}>
                <span style={styles.deviceInfoLabel}>Platform</span>
                <span style={styles.deviceInfoValue}>{deviceInfo.platform}</span>
              </div>
              <div style={styles.deviceInfoRow}>
                <span style={styles.deviceInfoLabel}>Language</span>
                <span style={styles.deviceInfoValue}>{deviceInfo.language}</span>
              </div>
              <div style={styles.deviceInfoRow}>
                <span style={styles.deviceInfoLabel}>Online</span>
                <span style={{ ...styles.deviceInfoValue, color: deviceInfo.onLine ? '#22c55e' : '#ef4444' }}>
                  {deviceInfo.onLine ? '✅ Sim' : '❌ Não'}
                </span>
              </div>
            </div>

            <div style={styles.deviceInfoCard}>
              <div style={styles.deviceInfoTitle}>📐 Tela</div>
              <div style={styles.deviceInfoRow}>
                <span style={styles.deviceInfoLabel}>Screen</span>
                <span style={styles.deviceInfoValue}>{deviceInfo.screenWidth}x{deviceInfo.screenHeight}</span>
              </div>
              <div style={styles.deviceInfoRow}>
                <span style={styles.deviceInfoLabel}>Viewport</span>
                <span style={styles.deviceInfoValue}>{deviceInfo.viewportWidth}x{deviceInfo.viewportHeight}</span>
              </div>
              <div style={styles.deviceInfoRow}>
                <span style={styles.deviceInfoLabel}>Pixel Ratio</span>
                <span style={styles.deviceInfoValue}>{deviceInfo.devicePixelRatio}x</span>
              </div>
              <div style={styles.deviceInfoRow}>
                <span style={styles.deviceInfoLabel}>Touch</span>
                <span style={{ ...styles.deviceInfoValue, color: deviceInfo.touchSupport ? '#22c55e' : '#94a3b8' }}>
                  {deviceInfo.touchSupport ? '✅ Suportado' : '❌ Não'}
                </span>
              </div>
            </div>

            <div style={styles.deviceInfoCard}>
              <div style={styles.deviceInfoTitle}>🎤 Mídia</div>
              <div style={styles.deviceInfoRow}>
                <span style={styles.deviceInfoLabel}>Audio Inputs</span>
                <span style={styles.deviceInfoValue}>{deviceInfo.mediaDevices.audioInputs}</span>
              </div>
              <div style={styles.deviceInfoRow}>
                <span style={styles.deviceInfoLabel}>Audio Outputs</span>
                <span style={styles.deviceInfoValue}>{deviceInfo.mediaDevices.audioOutputs}</span>
              </div>
              <div style={styles.deviceInfoRow}>
                <span style={styles.deviceInfoLabel}>Video Inputs</span>
                <span style={styles.deviceInfoValue}>{deviceInfo.mediaDevices.videoInputs}</span>
              </div>
              <div style={styles.deviceInfoRow}>
                <span style={styles.deviceInfoLabel}>Mic Permission</span>
                <span style={{
                  ...styles.deviceInfoValue,
                  color: deviceInfo.permissions.microphone === 'granted' ? '#22c55e' : '#f59e0b',
                }}>
                  {deviceInfo.permissions.microphone || 'unknown'}
                </span>
              </div>
            </div>

            {deviceInfo.connection && (
              <div style={styles.deviceInfoCard}>
                <div style={styles.deviceInfoTitle}>📶 Conexão</div>
                <div style={styles.deviceInfoRow}>
                  <span style={styles.deviceInfoLabel}>Type</span>
                  <span style={styles.deviceInfoValue}>{deviceInfo.connection.type || '-'}</span>
                </div>
                <div style={styles.deviceInfoRow}>
                  <span style={styles.deviceInfoLabel}>Effective</span>
                  <span style={styles.deviceInfoValue}>{deviceInfo.connection.effectiveType || '-'}</span>
                </div>
                <div style={styles.deviceInfoRow}>
                  <span style={styles.deviceInfoLabel}>Downlink</span>
                  <span style={styles.deviceInfoValue}>
                    {deviceInfo.connection.downlink ? `${deviceInfo.connection.downlink} Mbps` : '-'}
                  </span>
                </div>
                <div style={styles.deviceInfoRow}>
                  <span style={styles.deviceInfoLabel}>RTT</span>
                  <span style={styles.deviceInfoValue}>
                    {deviceInfo.connection.rtt ? `${deviceInfo.connection.rtt}ms` : '-'}
                  </span>
                </div>
              </div>
            )}

            {deviceInfo.memory && (
              <div style={styles.deviceInfoCard}>
                <div style={styles.deviceInfoTitle}>💾 Memória</div>
                <div style={styles.deviceInfoRow}>
                  <span style={styles.deviceInfoLabel}>Heap Used</span>
                  <span style={styles.deviceInfoValue}>
                    {Math.round((deviceInfo.memory.usedJSHeapSize || 0) / 1024 / 1024)} MB
                  </span>
                </div>
                <div style={styles.deviceInfoRow}>
                  <span style={styles.deviceInfoLabel}>Heap Total</span>
                  <span style={styles.deviceInfoValue}>
                    {Math.round((deviceInfo.memory.totalJSHeapSize || 0) / 1024 / 1024)} MB
                  </span>
                </div>
                <div style={styles.deviceInfoRow}>
                  <span style={styles.deviceInfoLabel}>Heap Limit</span>
                  <span style={styles.deviceInfoValue}>
                    {Math.round((deviceInfo.memory.jsHeapSizeLimit || 0) / 1024 / 1024)} MB
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <>
            <div style={styles.stats}>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{logs.length}</div>
                <div style={styles.statLabel}>Total Logs</div>
              </div>
              <div style={styles.statCard}>
                <div style={{ ...styles.statValue, color: '#ef4444' }}>
                  {stats.byLevel.error || 0}
                </div>
                <div style={styles.statLabel}>Erros</div>
              </div>
              <div style={styles.statCard}>
                <div style={{ ...styles.statValue, color: '#f59e0b' }}>
                  {stats.byLevel.warn || 0}
                </div>
                <div style={styles.statLabel}>Warnings</div>
              </div>
              <div style={styles.statCard}>
                <div style={{ ...styles.statValue, color: '#22c55e' }}>
                  {stats.networkSuccess}
                </div>
                <div style={styles.statLabel}>Requests OK</div>
              </div>
              <div style={styles.statCard}>
                <div style={{ ...styles.statValue, color: '#ef4444' }}>
                  {stats.networkError}
                </div>
                <div style={styles.statLabel}>Requests Failed</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{stats.avgDuration}ms</div>
                <div style={styles.statLabel}>Avg Response Time</div>
              </div>
            </div>

            <div style={{ padding: '16px' }}>
              <h3 style={{ color: '#94a3b8', marginBottom: '12px' }}>Logs por Categoria</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {Object.entries(stats.byCategory)
                  .sort((a, b) => b[1] - a[1])
                  .map(([cat, count]) => (
                    <span
                      key={cat}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#1e293b',
                        borderRadius: '6px',
                        fontSize: '11px',
                      }}
                    >
                      {cat}: <strong>{count}</strong>
                    </span>
                  ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Copy Success Toast */}
      {showCopySuccess && (
        <div style={styles.copySuccess}>
          ✅ Logs copiados para a área de transferência!
        </div>
      )}
    </div>
  );
}

export default DebugPanel;
